import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import { backendRoutes, frontendRoutes } from '../../../../Constants/constants';
import Loader from '../../../Loader/Loader';
import fingerprint from '../../../../assets/images/icn-fingerprint.svg';
import { Button } from '../../../Button/Button';
import LockIcon from '../../../../assets/images/icn-lock-open.svg';
import { Spinner } from 'react-bootstrap';
import errorImage from '../../../../assets/images/error-img.svg';
import { ReactComponent as BackIcon } from '../../../../assets/images/icn-arrow-prev.svg';
import { convertDistance, getDistance } from 'geolib';
import { BuilderSettings } from '../../../../redux/types/builders';
import { fpGetEvent } from '../../../../utils/fingerprint';
import { getCustomerIdFromToken } from '../../../../utils';
import {
  PROPERTY_STORAGE_KEYS,
  clearStoredPropertyContext,
  createPropertyError,
  getTourArrivalSource,
} from '../../../../utils/propertyRecovery';
import { clearPropertyContext, get_property_details } from '../../../../redux/action/action';
import { useTranslation } from 'react-i18next';
import { usePropertyResolution } from '../../../../hooks/usePropertyResolution';
import { getWebflowCode } from '../../../../Constants/api';
import TourAccessLoader from './TourAccessLoader';

type LockInstructions = {
  lockCode: string;
  step1Text: string;
  step2Text: string;
  manufacturer: string;
  isUnlocking: boolean;
  tourHours: {
    tourHoursWindow: string;
    isOutsideTourHours: boolean;
    isToursAvailable: boolean;
  }
};

const LockScreen = () => {
  const userDetails = useSelector((state: any) => state.register.register);
  const property_details = useSelector((state: any) => state.property.property);
  const attribution = useSelector((state: any) => state.builder.attribution);
  const currentPropertyId = (property_details && property_details.id)
    || localStorage.getItem(PROPERTY_STORAGE_KEYS.propertyId);
  // Recovering from an error clears the stored context, so keep the id the screen started with.
  const initialPropertyIdRef = useRef(currentPropertyId);
  const property_id = currentPropertyId || initialPropertyIdRef.current;
  const propertyName = property_details?.marketingName || property_details?.address;
  const hasPropertyDetails = Boolean(property_details);
  const user_token = localStorage.getItem('user_token');
  const propertyBuilder = useSelector((state: any) => state?.property?.property?.builder);
  const builderSettings: BuilderSettings = useSelector((state: any) => state.builder.settings);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    error: accessError,
    clearError,
    reportError,
    loadProperty,
    findOtherProperties,
  } = usePropertyResolution();
  const isAdcHub = property_details?.hub_type === 'ADC'
    || property_details?.hub_serial_number?.includes('ADC');
  const isAdcDisarmUnlockEnabled = Boolean(property_details?.adc_disarm_unlock_enabled);
  const isAdcAccessFlow = isAdcHub && isAdcDisarmUnlockEnabled;
  const isBleLock = property_details?.connection_mode === 'ble';
  const hasRemoteSmartLock = Boolean(property_details?.smart_lock_serial_number) && !isBleLock;
  const shouldDelayAnimation = isAdcAccessFlow && Boolean(property_details?.delay_animation);

  const [message, setMessage] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAccessLoaderDone, setIsAccessLoaderDone] = useState(true);
  const [accessLoaderRunKey, setAccessLoaderRunKey] = useState(0);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [isCheckingLockDetails, setIsCheckingLockDetails] = useState(false);
  const [isLocked, setIslocked] = useState(true);
  const [seconds, setSeconds] = useState(1);
  const [lockInstructions, setLockInstructions] = useState<LockInstructions>({
    lockCode: '',
    step1Text: '',
    step2Text: '',
    manufacturer: '',
    isUnlocking: false,
    tourHours: {
      tourHoursWindow: "",
      isOutsideTourHours: false,
      isToursAvailable: true
    }
  });
  const [distance, setDistance] = useState(0);
  const [delayAnimationState, setDelayAnimationState] = useState(0);
  const requestInFlightRef = useRef(false);
  const skipNextAutomaticRequestRef = useRef(false);

  let params = useParams<{ builder_name?: string }>();

  const history = useHistory();
  const previousPath = () => {
    history.goBack();
  };
  const shouldEnterProperty = accessError?.code === 'PROPERTY_ID_INVALID';
  const canRetry = accessError ? accessError.retryable || shouldEnterProperty : true;
  const shouldHideAdcAccessContent = isAdcHub && showAlert;

  const shouldShowAccessLoader = !shouldHideAdcAccessContent
    && isAdcAccessFlow
    && accessLoaderRunKey > 0
    && (!isAccessLoaderDone || isLoading);

  const canConfirmEntry =
    !shouldShowAccessLoader &&
    !isLoading &&
    Boolean(lockInstructions.lockCode);

  const handleAccessLoaderDone = useCallback(() => {
    setIsAccessLoaderDone(true);
  }, []);

  const getPropertyCode = useCallback(async (isRetry = false) => {
    if (requestInFlightRef.current) return;

    requestInFlightRef.current = true;
    if (isAdcAccessFlow) {
      setIsAccessLoaderDone(false);
      setAccessLoaderRunKey(key => key + 1);
    } else {
      startDelayAnimation();
    }
    setIsLoading(true);
    setShowAlert(false);
    clearError();

    const linkedId = getCustomerIdFromToken(user_token);
    const requestContext = {
      arrivalSource: getTourArrivalSource(isRetry),
    };

    try {
      if (!property_id) {
        throw createPropertyError({
          code: 'PROPERTY_ID_INVALID',
          message: 'Lock screen opened without a property in state or storage',
        });
      }

      if (isRetry || !hasPropertyDetails) {
        const property = await loadProperty({
          propertyId: property_id,
          customerId: linkedId,
          propertyName,
          isRetry,
          forceRehydrate: true,
        });

        if (!property) {
          setShowAlert(true);
          return;
        }

        skipNextAutomaticRequestRef.current = true;
        dispatch(get_property_details({ id: Number(property_id), ...property }));
      }

      void fpGetEvent({ linkedId });

      const {
        code: lockCode,
        step1Text,
        step2Text,
        manufacturer,
        isUnlocking: isAdcUnlocking,
        visitId,
        tourHours,
      } = await getWebflowCode(property_id, attribution, user_token, requestContext);

      setLockInstructions({
        lockCode,
        step1Text,
        step2Text,
        manufacturer,
        isUnlocking: isAdcUnlocking === true,
        tourHours,
      });
      localStorage.setItem(PROPERTY_STORAGE_KEYS.visitId, String(visitId));
      localStorage.setItem(PROPERTY_STORAGE_KEYS.propertyId, String(property_id));
    } catch (err) {
      setShowAlert(true);
      reportError(err, {
        propertyId: property_id || undefined,
        propertyName,
        customerId: linkedId,
        isRetry,
      });
    } finally {
      setIsLoading(false);
      requestInFlightRef.current = false;
    }
  }, [
    property_id,
    user_token,
    attribution,
    dispatch,
    propertyName,
    hasPropertyDetails,
    clearError,
    reportError,
    loadProperty,
    isAdcAccessFlow,
  ]);

  const handleTryAgain = () => {
    if (shouldEnterProperty) {
      clearStoredPropertyContext();
      dispatch(clearPropertyContext());
      history.push(frontendRoutes.enterProperty(params.builder_name).path);
      return;
    }

    if (accessError?.category === 'expired_session') {
      localStorage.removeItem('user_token');
      history.push(frontendRoutes.signIn(params.builder_name).path, {
        nextPath: frontendRoutes.selectTourDate(params.builder_name, property_id).path,
      });
      return;
    }

    getPropertyCode(true);
  };

  const handleGetPosition = useCallback(({ coords }: { coords: { latitude: number; longitude: number } }) => {
    if (property_details) {
        const currentPosition = {
        latitude: coords.latitude,
        longitude: coords.longitude,
      };
      const propertyPosition = {
        latitude: property_details.latitude,
        longitude: property_details.longitude,
      };

      const distance = getDistance(currentPosition, propertyPosition);
      const distanceInMiles = convertDistance(distance, 'mi');

      if (distanceInMiles > 8) {
        setDistance(distanceInMiles);
        setIsLoading(false);
        setIsAccessLoaderDone(true);
        return;
      }

      getPropertyCode();
    }
  },[property_details, getPropertyCode]);

  const startDelayAnimation = () => {
    setDelayAnimationState(1);
    setTimeout( () => {
      setDelayAnimationState(2);
      setTimeout( () => {
        setDelayAnimationState(3);
        setTimeout( () => {
          setDelayAnimationState(4);
        }, 3000)
      }, 2000)
    }, 4000)
  }


  useEffect(() => {
    if (skipNextAutomaticRequestRef.current) {
      skipNextAutomaticRequestRef.current = false;
      return;
    }

    setIsLoading(true);
    const isDemoMode = propertyBuilder?.toLowerCase() === 'demo mode';
    if (isDemoMode || !builderSettings.geolocation_active) {
      getPropertyCode();
    } else {
      if (property_details) {
        navigator.geolocation.getCurrentPosition(
            handleGetPosition,
            error => {
              console.log(error);
              setIsLoading(false);
              setIsAccessLoaderDone(true);
              history.push(frontendRoutes.shareLocation(params.builder_name).path)
            },
            {
              enableHighAccuracy: true,
            },
          );
      } else {
        getPropertyCode();
      }
    }

  }, [property_details, handleGetPosition, getPropertyCode, propertyBuilder, history, params.builder_name, builderSettings.geolocation_active]);

  useEffect(() => {
    const checkLockDetails = async () => {
      try {
        setIsCheckingLockDetails(true);

        const { data } = await axios({
          baseURL: backendRoutes.MOBILE_BASE_URL,
          url: `properties/${property_id}/lock-live-details`,
          method: 'GET',
          headers: {
            'x-auth-token': user_token,
          },
        });

        setIsCheckingLockDetails(false);
        setIslocked(data.locked);
      } catch (err) {
        setIsCheckingLockDetails(false);
        setIsUnlocking(false);
        setShowAlert(true);

        if (err instanceof Error) {
          const msg = err.message;
          setMessage(msg);
          return;
        }

        console.log('Unexpected error', err);
      }
    };

    if (hasRemoteSmartLock && !isLoading) {
      setIsUnlocking(true);

      const timer = setTimeout(() => {
        if (seconds < 15) {
          setSeconds(seconds => seconds + 1);
        }

        if (!isCheckingLockDetails) {
          checkLockDetails();
        }
      }, 1000);

      if ((!isLocked || seconds >= 15) && !isCheckingLockDetails) {
        setIsUnlocking(false);
        clearInterval(timer);
      }

      return () => {
        clearInterval(timer);
      };
    }
  }, [
    isCheckingLockDetails,
    isLoading,
    isLocked,
    hasRemoteSmartLock,
    property_id,
    seconds,
    user_token,
  ]);

  if (distance > 8) {
    return (
      <section className="main-section d-flex justify-content-center">
        <div className="box">
          <div className="status-title error">
            <h1>Uh-Oh</h1>
            <span>It appears that you have not yet arrived at your destination.</span>
            <div>
              <span onClick={() => history.goBack()} className="a">
                Back
              </span>
              <Link to={frontendRoutes.selectTourDate(params.builder_name, property_id).path}>
                Reschedule
              </Link>
              <Link to={frontendRoutes.chooseCommunity(params.builder_name).path}>Start Over</Link>
            </div>
          </div>
          <h2 className="title">You’re just a short {Math.ceil(distance)} miles away!</h2>
          <div className="text-container">
            <p className="text">
              Please return when you arrive on-site to continue your self-tour. For help, call
              NterNow support at{' '}
              <a href="tel:+1-844-531-1283">(844) 531-1283</a>.
            </p>
          </div>
          <a href="tel:+1-844-531-1283" className="need-help-link" target="_blank" rel="noreferrer">
            Need Help? <i className="icn icn-phone"></i>
          </a>
          <div style={{ padding: '96px 40px 60px 40px' }}>
            <Button
              onClick={() => {
                history.push(frontendRoutes.selectTourDate(params.builder_name, property_id).path);
              }}
              className="button back-button mb-5"
            >
              <BackIcon />
              Back
            </Button>
            <p className="text">
              Need help with your self-tour? Call NterNow support at{' '}
              <a href="tel:+1-844-531-1283">(844) 531-1283</a>.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="main-section success-screen tour-success-screen d-flex justify-content-center">
      {(!lockInstructions.tourHours.isOutsideTourHours && lockInstructions.tourHours.isToursAvailable) && (<div className="box">
        <div className="status-title success">
          <h1>SUCCESS!</h1>
          <span>
            Hi {userDetails?.firstName}! You are all set to tour:{' '}
            {property_details?.marketingName ? `${property_details?.marketingName}, ` : ''}
            {property_details?.address ?? ''}
          </span>
          <div className='d-flex gap-3 align-items-center'>
            <span onClick={previousPath} className="a m-0">
              Back
            </span>
            {/* {params.builder_name !== noBuilderTour.TOURS && <> */}
              <Link
                to={frontendRoutes.selectTourDate(params.builder_name, property_id).path}
                className=""
              >
                Reschedule
              </Link>
              <Link to={frontendRoutes.chooseCommunity(params.builder_name).path} className="">
                Start Over
              </Link>
            {/* </>} */}
          </div>
        </div>
        <div className="box-data d-flex flex-column text-center">
          <div className="my-auto">
            {!hasRemoteSmartLock && (
              <>
                {shouldShowAccessLoader ? (
                  <TourAccessLoader
                    runKey={accessLoaderRunKey}
                    onDone={handleAccessLoaderDone}
                    shouldDelayAnimation={shouldDelayAnimation}
                    isRequestLoading={isLoading}
                    isUnlocking={lockInstructions.isUnlocking}
                  />
                ) : (
                <>
                {showAlert === false ? (
                  <>
                    <p className="lock-instructions">{lockInstructions?.step1Text}</p>
                    <p className="lock-instructions">{lockInstructions?.step2Text}</p>
                  </>
                ) : (
                  <div className="mb-4">
                    <h2 className="text-black fs-3 mb-2">Access Information</h2>
                    {!shouldEnterProperty && (propertyName || property_id) && (
                      <p className="text-gray-dark">
                        {propertyName || `${t('property_label')}: ${property_id}`}
                      </p>
                    )}
                    <div className="error-container">
                      <div className="error-img">
                        <img width="20" src={errorImage} alt="" />
                      </div>
                      <h1 className="error-title mb-2">
                        {accessError ? t(accessError.messageKey) : message}
                      </h1>
                      <span className="text-gray-dark">
                        {t(canRetry ? 'access_problem_retry_help' : 'access_problem_terminal_help')}
                      </span>
                      {canRetry && (
                        <Button
                          onClick={handleTryAgain}
                          disabled={isLoading}
                          className="button w-100 mt-3"
                        >
                          {accessError?.category === 'expired_session' ? t('sign_in') : t('try_again')}
                        </Button>
                      )}
                      <Button
                        onClick={findOtherProperties}
                        className="button back-button w-100 mt-3"
                      >
                        {t('find_other_properties')}
                      </Button>
                    </div>
                  </div>
                )}

                {lockInstructions?.manufacturer === 'lockly' && (
                  <>
                    <label className="lock-code">
                      {lockInstructions?.lockCode}
                      <img width="20" src={fingerprint} alt="" />
                    </label>
                    <span>then</span>
                    <label className="lock-code">
                      {lockInstructions?.lockCode}
                      <img width="20" src={fingerprint} alt="" />
                    </label>
                  </>
                )}

                {!hasRemoteSmartLock && isAdcHub ?
                  (
                    !shouldHideAdcAccessContent &&
                    <>
                        {isAdcAccessFlow ? (
                          <>
                            <h2 className="h1">
                              {lockInstructions.isUnlocking
                                ? 'Door Unlocked'
                                : 'Unable to Unlock'}
                            </h2>
                            {lockInstructions.lockCode && (
                              <>
                                <p className="h1" style={{ margin: '20px 0' }}>
                                  Backup code below is valid for 15 minutes. Use it if the door does not unlock automatically.
                                </p>
                                <label className="lock-code">{lockInstructions.lockCode}</label>
                              </>
                            )}
                      </>
                    ) : (
                    <>
                    {delayAnimationState === 0 && (
                      <></>
                    )}
                    {delayAnimationState === 1 && (
                      <>
                      <h2 className="h1">Adding Code ...</h2>
                      <div className="bouncing-loader">
                        <div></div>
                        <div></div>
                        <div></div>
                      </div>
                      </>
                    )}
                    {delayAnimationState === 2 && (
                      <>
                      <h2 className="h1">Disarming Alarm ...</h2>
                      <div className="bouncing-loader">
                        <div></div>
                        <div></div>
                        <div></div>
                      </div>
                      </>
                    )}
                    {delayAnimationState === 3 && (
                      <>
                      <h2 className="h1">Code can take up 30 seconds to work on lock.</h2>
                      <div className="bouncing-loader">
                        <div></div>
                        <div></div>
                        <div></div>
                      </div>
                      </>
                    )}
                    {delayAnimationState === 4 && (
                      <>
                      <h2 className="h1">Your lock code is:</h2>
                      <label className="lock-code">
                        {lockInstructions?.lockCode}
                        {lockInstructions?.manufacturer === 'igloohome' && (
                          <img src={LockIcon} alt="lock" />
                        )}
                      </label>
                      </>
                    )}
                    </>
                    )}
                    </>
                    )
                  :
                  (
                  <>
                  <h2 className="h1">Your lock code is:</h2>
                  <label className="lock-code">
                    {lockInstructions?.lockCode}
                    {lockInstructions?.manufacturer === 'igloohome' && (
                      <img src={LockIcon} alt="lock" />
                    )}
                  </label>
                  </>
              ) }

                </>
                )}
              </>
            )}
            {hasRemoteSmartLock && (
              <>
                {!isLoading && isUnlocking && (
                  <>
                    <p className="h1" style={{ marginBottom: '40px' }}>
                      Unlocking Door... Please stand by
                    </p>
                    <Spinner animation="border" style={{ marginBottom: '40px' }} />
                  </>
                )}
                {!isUnlocking && (
                  <>
                    <h2 className="h1" style={{ fontSize: '40px', lineHeight: '48px' }}>
                      {`${
                        isLocked
                          ? 'Unable to Unlock. Please try the code'
                          : 'Door Unlocked. Please enjoy your visit'
                      }`}
                    </h2>
                    <p className="h1" style={{ margin: '20px 0' }}>
                      ...
                    </p>
                    <p className="h1">
                      Backup code below is valid for 10 minutes. Please use it if the door did not
                      unlock automatically.
                    </p>
                    <label className="lock-code">{lockInstructions?.lockCode}</label>
                  </>
                )}
              </>
            )}
            {!shouldShowAccessLoader && <span>
              Unable to unlock?{' '}
              <a href="tel:+1-844-531-1283" className="" target="_blank" rel="noreferrer">
                Help <i className="icn icn-phone"></i>
              </a>
            </span>}
            {!shouldHideAdcAccessContent && property_details?.access_notes && !shouldShowAccessLoader && (
            <div className='access-notes' style={{ marginTop: '50px', padding: '10px 0' }}>
              <h1 className='h1' >Access Notes: </h1>
              <h1 className='h1' style={{ marginTop: '20px' }}>
                {property_details.access_notes}
              </h1>
            </div>
            )}
          </div>
          {isLoading && (
            <>
              {!hasRemoteSmartLock && isAdcHub ?
              (<></>)
              :
              (<Loader />)
          }
              {hasRemoteSmartLock && (
                <p
                  style={{
                    position: 'absolute',
                    bottom: '420px',
                    left: '160px',
                    zIndex: 999,
                    fontSize: '30px',
                    color: '#fff',
                  }}
                >
                  Unlocking...
                </p>
              )}
            </>
          )}
          <div className="box-footer">
            {!shouldHideAdcAccessContent && (
            <Link
              to={frontendRoutes.feedback(params.builder_name).path}
              className="w-100"
              onClick={event => {
                if (!canConfirmEntry) {
                  event.preventDefault();
                }
              }}
            >
              <Button disabled={!canConfirmEntry} className="button w-100">I'm in</Button>
            </Link>
            )}
            <p>
              Need help with your self-tour? Call NterNow support at{' '}
              <a href="tel:+1-844-531-1283">(844) 531-1283</a>.
            </p>
          </div>
        </div>
      </div>)}
      {(lockInstructions.tourHours.isOutsideTourHours || !lockInstructions.tourHours.isToursAvailable) && (<div className="box">
        <div className="status-title error">
          <h1>Unable to access!</h1>
          <span>
            Hi {userDetails?.firstName}! You cannot access:{' '}
            {property_details?.marketingName ? `${property_details?.marketingName}, ` : ''}
            {property_details?.address ?? ''} at this time .
          </span>
          <div>
            <span onClick={previousPath} className="a">
              Back
            </span>
          </div>
        </div>
        <div className="box-data d-flex flex-column text-center">
          <div className="my-auto">
            {!lockInstructions.tourHours.isToursAvailable && <div>
              <label className="lock-code">
              Tours are not available this day.
              </label>
              &nbsp;
              &nbsp;
            </div>}
            {lockInstructions.tourHours.isOutsideTourHours && <div>
              <label className="lock-code">
                Outside tour hours!
              </label>
              <h2 className="h1">Tours are only available during these hours:</h2>
              &nbsp;
              <h2 className="h1">{lockInstructions.tourHours.tourHoursWindow}</h2>
              &nbsp;
            </div>}
            <span>
              Unable to unlock?{' '}
              <a href="tel:+1-844-531-1283" className="" target="_blank" rel="noreferrer">
                Help <i className="icn icn-phone"></i>
              </a>
            </span>
          </div>

          <div className="box-footer">
            <p>
              Need help with your self-tour? Call NterNow support at{' '}
              <a href="tel:+1-844-531-1283">(844) 531-1283</a>.
            </p>
          </div>
        </div>
      </div>)}

    </section>
  );
};

export default LockScreen;
